const PAGE_WIDTH = 760;
const PAGE_MIN_HEIGHT = 1090;

const escapeHtml = (value = "") =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const wrapSection = (title, content = "") =>
  !content
    ? ""
    : `<section class="pdf-section"><h2>${escapeHtml(title)}</h2>${content}</section>`;

const buildKeyValueTable = (rows = [], firstLabel, secondLabel) => {
  if (!rows.length) return "";

  return `<table class="pdf-table"><thead><tr><th>${escapeHtml(firstLabel)}</th><th>${escapeHtml(
    secondLabel
  )}</th></tr></thead><tbody>${rows
    .map((row) => `<tr><td>${escapeHtml(row.label)}</td><td>${escapeHtml(row.value)}</td></tr>`)
    .join("")}</tbody></table>`;
};

const buildNumericalTable = (numericalResults, labels) => {
  if (!numericalResults?.rows?.length) return "";

  const note = numericalResults.description
    ? `<p class="pdf-note">${escapeHtml(numericalResults.description)}</p>`
    : "";

  return `${note}<table class="pdf-table"><thead><tr>
    <th>${escapeHtml(numericalResults.columns.item || labels.item)}</th>
    <th>${escapeHtml(numericalResults.columns.metric || labels.metric)}</th>
    <th>${escapeHtml(numericalResults.columns.value || labels.value)}</th>
    <th>${escapeHtml(numericalResults.columns.change || labels.change)}</th>
  </tr></thead><tbody>${numericalResults.rows
    .map(
      (row) =>
        `<tr><td>${escapeHtml(row.item)}</td><td>${escapeHtml(row.metric)}</td><td>${escapeHtml(
          row.value
        )}</td><td>${escapeHtml(row.change)}</td></tr>`
    )
    .join("")}</tbody></table>`;
};

const buildList = (items = [], ordered = false) => {
  if (!items.length) return "";
  const tag = ordered ? "ol" : "ul";
  return `<${tag} class="pdf-list">${items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</${tag}>`;
};

const buildHeader = ({ model, labels }) => {
  const locale = model.language === "en" ? "en-US" : "ar-SA";
  const createdAt = new Date(model.generatedAt).toLocaleString(locale);

  return `
    <header class="pdf-header">
      <div class="pdf-title-wrap">
        <h1>${escapeHtml(model.reportTitle)}</h1>
        ${model.reportSubtitle ? `<div class="pdf-subtitle">${escapeHtml(model.reportSubtitle)}</div>` : ""}
        ${model.reportDescription ? `<div class="pdf-description">${escapeHtml(model.reportDescription)}</div>` : ""}
      </div>
      <div class="pdf-meta-box">
        <div class="pdf-meta-label">${escapeHtml(labels.createdAt)}</div>
        <div class="pdf-meta-value">${escapeHtml(createdAt)}</div>
      </div>
    </header>`;
};

export const buildPdfDocumentMarkup = ({ model, t }) => {
  const language = model.language;
  const labels = {
    createdAt: t("reports.createdAt", language === "en" ? "Created At" : "تاريخ الإنشاء"),
    reportMeta: t("reports.reportMeta", language === "en" ? "Report Meta" : "بيانات التقرير"),
    basicReportData: t("reports.basicReportData", language === "en" ? "Basic Report Data" : "بيانات التقرير الأساسية"),
    numericalResults: t("reports.numericalResults", language === "en" ? "Numerical Results" : "نتائج رقمية"),
    analysis: t("reports.analysis", language === "en" ? "Analysis" : "التحليل"),
    recommendationsAndAlerts: t(
      "reports.recommendationsAndAlerts",
      language === "en" ? "Recommendations & Alerts" : "التوصيات والتنبيهات"
    ),
    field: t("reports.field", language === "en" ? "Field" : "الحقل"),
    value: t("reports.value", language === "en" ? "Value" : "القيمة"),
    item: t("reports.item", language === "en" ? "Item" : "البند"),
    metric: t("reports.metric", language === "en" ? "Metric" : "النسبة"),
    change: t("reports.change", language === "en" ? "Change" : "التغير"),
  };

  const body = model.aiServiceFailure
    ? wrapSection(
        t("reports.aiAnalysisFailed", language === "en" ? "AI analysis failed" : "فشل التحليل الذكي"),
        `<p class="pdf-error">${escapeHtml(model.userFacingAiError)}</p>`
      )
    : [
        wrapSection(labels.reportMeta, buildKeyValueTable(model.reportMeta, labels.field, labels.value)),
        wrapSection(labels.basicReportData, buildKeyValueTable(model.basicReportData, labels.field, labels.value)),
        wrapSection(labels.numericalResults, buildNumericalTable(model.numericalResults, labels)),
        wrapSection(labels.analysis, buildList(model.analysis, true)),
        wrapSection(labels.recommendationsAndAlerts, buildList(model.recommendationsAndAlerts)),
      ]
        .filter(Boolean)
        .join("");

  return `
  <style>
    body { margin: 0; background: #edf2f7; }
    .pdf-document { padding: 28px; background: #edf2f7; width: max-content; }
    .pdf-page {
      width: ${PAGE_WIDTH}px; min-height: ${PAGE_MIN_HEIGHT}px; box-sizing: border-box;
      background: #fff; color: #1a202c; padding: 54px 56px 40px; border-radius: 24px;
      font-family: Arial, "Segoe UI", Tahoma, sans-serif;
    }
    .pdf-header { display: grid; grid-template-columns: minmax(0,1fr) 220px; gap: 18px; margin-bottom: 24px; align-items: start; }
    .pdf-title-wrap { text-align: center; }
    .pdf-title-wrap h1 { margin: 0; font-size: 32px; line-height: 1.35; }
    .pdf-subtitle { margin-top: 10px; font-size: 20px; font-weight: 700; color: #2d3748; }
    .pdf-description { margin-top: 8px; font-size: 14px; color: #718096; }
    .pdf-meta-box { border: 1px solid #cbd5e0; border-radius: 16px; padding: 14px 16px; background: #f8fbff; }
    .pdf-meta-label { font-size: 11px; color: #718096; margin-bottom: 6px; }
    .pdf-meta-value { font-size: 13px; font-weight: 700; }
    .pdf-section { margin-bottom: 18px; border: 1px solid #dbeafe; border-radius: 16px; overflow: hidden; }
    .pdf-section h2 { margin: 0; padding: 14px 18px; background: #4f86ad; color: white; font-size: 18px; }
    .pdf-section > *:not(h2) { padding: 16px 18px; }
    .pdf-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    .pdf-table th, .pdf-table td { border: 1px solid #e2e8f0; padding: 10px; font-size: 12px; line-height: 1.7; vertical-align: top; }
    .pdf-table th { background: #f8fafc; font-weight: 700; }
    .pdf-list { margin: 0; padding-inline-start: 22px; }
    .pdf-list li { margin-bottom: 8px; line-height: 1.9; }
    .pdf-note { margin: 0 0 14px; color: #718096; font-size: 13px; }
    .pdf-error { margin: 0; color: #c53030; font-size: 14px; line-height: 1.9; }
  </style>
  <div class="pdf-document">
    <div class="pdf-page" dir="${model.direction}">
      ${buildHeader({ model, labels })}
      ${body}
    </div>
  </div>`;
};
